import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, CheckCircle, Clock, Package } from 'lucide-react';
import Barcode from 'react-barcode';

const API = process.env.REACT_APP_BACKEND_URL;

export const AllgemeinExportView = () => {
  const { exportId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState(null);

  useEffect(() => {
    fetchExportDetails();
  }, [exportId]);

  const fetchExportDetails = async () => {
    try {
      const response = await axios.get(`${API}/api/general-exports/${exportId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setExportData(response.data);
    } catch (error) {
      console.error('Failed to fetch export details:', error);
      toast.error('Fehler beim Laden des Exports');
      navigate('/admin/allgemein-export');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/api/general-exports/${exportId}/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `allgemein_export_${exportId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF wird heruntergeladen');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Fehler beim Herunterladen');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Lädt...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!exportData) {
    return null;
  }

  // Group writeoffs by product for summary view
  const groupedData = exportData.export_format === 'summary' 
    ? exportData.writeoffs.reduce((acc, w) => {
        const key = `${w.product_name}_${w.article_number || ''}_${w.ean || ''}`;
        if (!acc[key]) {
          acc[key] = {
            product_name: w.product_name,
            article_number: w.article_number,
            ean: w.ean,
            quantity: 0,
            type_names: new Set()
          };
        }
        acc[key].quantity += w.quantity;
        acc[key].type_names.add(w.type_name);
        return acc;
      }, {})
    : null;

  const summaryData = groupedData 
    ? Object.values(groupedData).map(item => ({
        ...item,
        type_names: Array.from(item.type_names).join(', ')
      }))
    : exportData.writeoffs;

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/allgemein-export')}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Export-Details
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {new Date(exportData.start_date).toLocaleDateString('de-DE')} - {new Date(exportData.end_date).toLocaleDateString('de-DE')}
            </p>
          </div>
          <Button onClick={handleDownloadPDF}>
            <Download className="h-4 w-4 mr-2" />
            PDF herunterladen
          </Button>
        </div>

        {/* Info Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Status</p>
                <Badge variant={exportData.status === 'completed' ? 'default' : 'secondary'}>
                  {exportData.status === 'completed' ? (
                    <>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Abgeschlossen
                    </>
                  ) : (
                    <>
                      <Clock className="h-3 w-3 mr-1" />
                      Ausstehend
                    </>
                  )}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Anzahl Abschriften</p>
                <p className="font-semibold">{exportData.writeoff_count}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Format</p>
                <p className="font-semibold">
                  {exportData.export_format === 'summary' ? 'Zusammenfassung' : 'Detailliert'}
                </p>
              </div>
            </div>
            {exportData.completion_note && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Notiz:</strong> {exportData.completion_note}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">
              {exportData.export_format === 'summary' ? 'Zusammenfassung' : 'Alle Abschriften'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    {exportData.export_format === 'detailed' && (
                      <th className="text-left p-3 font-semibold">Datum</th>
                    )}
                    <th className="text-left p-3 font-semibold">Produktname</th>
                    <th className="text-left p-3 font-semibold">Art.-Nr.</th>
                    <th className="text-left p-3 font-semibold">EAN</th>
                    <th className="text-left p-3 font-semibold">Menge</th>
                    <th className="text-left p-3 font-semibold">Typ</th>
                    {exportData.include_barcode && (
                      <th className="text-left p-3 font-semibold">Barcode</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((item, index) => {
                    const timestamp = exportData.export_format === 'detailed' && item.timestamp
                      ? new Date(item.timestamp).toLocaleString('de-DE')
                      : null;
                    
                    return (
                      <tr key={index} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                        {exportData.export_format === 'detailed' && (
                          <td className="p-3">{timestamp}</td>
                        )}
                        <td className="p-3 font-medium">{item.product_name}</td>
                        <td className="p-3">{item.article_number || '-'}</td>
                        <td className="p-3">{item.ean || '-'}</td>
                        <td className="p-3">{item.quantity}</td>
                        <td className="p-3">
                          {exportData.export_format === 'summary' ? item.type_names : item.type_name}
                        </td>
                        {exportData.include_barcode && (
                          <td className="p-3">
                            {item.article_number ? (
                              <div className="max-w-[200px]">
                                <Barcode
                                  value={item.article_number}
                                  width={1.5}
                                  height={40}
                                  fontSize={10}
                                  displayValue={true}
                                />
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {summaryData.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Keine Abschriften gefunden</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
